import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

const TAB_QUERY_KEY = 'tab'

export const resolveTabFromSearchParams = <T extends string>(
	searchParams: URLSearchParams,
	tabValues: readonly T[],
	defaultTab: T,
): T => {
	const tab = searchParams.get(TAB_QUERY_KEY)

	if (!tab) {
		return defaultTab
	}

	return tabValues.includes(tab as T) ? (tab as T) : defaultTab
}

export const buildSearchParamsWithTab = (
	searchParams: URLSearchParams,
	nextTab: string,
) => {
	const nextSearchParams = new URLSearchParams(searchParams)
	nextSearchParams.set(TAB_QUERY_KEY, nextTab)

	return nextSearchParams
}

export const useQueryParamTab = <T extends string>(
	tabValues: readonly T[],
	defaultTab: T,
) => {
	const [searchParams, setSearchParams] = useSearchParams()
	const activeTabFromUrl = resolveTabFromSearchParams(
		searchParams,
		tabValues,
		defaultTab,
	)
	const serializedTabValues = JSON.stringify(tabValues)

	const resolvedTabValues = useMemo(
		() => tabValues,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[serializedTabValues],
	)

	const handleTabChange = useCallback(
		(value: string | null) => {
			if (!value || !resolvedTabValues.includes(value as T)) {
				return
			}

			const nextTab = value as T
			if (nextTab === activeTabFromUrl) {
				return
			}

			setSearchParams((prev) => buildSearchParamsWithTab(prev, nextTab), {
				replace: true,
			})
		},
		[activeTabFromUrl, resolvedTabValues, setSearchParams],
	)

	return {
		activeTab: activeTabFromUrl,
		handleTabChange,
	}
}
